import { substitute } from 'js/helpers/i18n';
import { CmsBrowsePage } from 'js/model/cms/cms-browse-page';
import {
  Location,
  VenueListingSpecificationOutput,
} from 'js/model/rainbow/browse-page/VenueListingSpecificationOutput';
import { namedEntityOutputName } from 'js/model/rainbow/NamedEntityOutput';

const nbsp = '\u00A0'; // non-breaking space

export interface CmsBrowseResultsSummary {
  page: {
    browse: {
      'results-summary': CmsBrowsePage['page']['browse']['results-summary'];
    };
  };
  entities: Pick<CmsBrowsePage['entities'], 'any-type'>;
}

export function generateNonTreatmentWhat(
  cms: CmsBrowseResultsSummary,
  specification: VenueListingSpecificationOutput,
  resultCount: number,
  titleOverride?: string
): string | null {
  if (titleOverride) {
    return titleOverride;
  }

  if (specification.menuItemTag !== undefined) {
    return specification.menuItemTag.name;
  }

  if (specification.venueTag !== undefined) {
    return specification.venueTag.name;
  }

  if (specification.chain !== undefined) {
    return specification.chain.name;
  }

  if (
    specification.productBrands !== undefined &&
    specification.productBrands.length > 0
  ) {
    return specification.productBrands[0].name;
  }

  if (specification.venueType !== undefined) {
    return namedEntityOutputName(specification.venueType, resultCount);
  }

  return null;
}

export function generateWhat(
  cms: CmsBrowseResultsSummary,
  specification: VenueListingSpecificationOutput,
  resultCount: number,
  titleOverride?: string
): string {
  const nonTreatmentWhat = generateNonTreatmentWhat(
    cms,
    specification,
    resultCount,
    titleOverride
  );
  if (nonTreatmentWhat) {
    return nonTreatmentWhat;
  }

  if (specification.treatmentCategories !== undefined) {
    // If more than one treatment, fallthrough to use the
    // treatments' treatment type first item
    if (specification.treatmentCategories.length === 1) {
      return namedEntityOutputName(
        specification.treatmentCategories[0],
        resultCount
      );
    }
  }

  if (specification.treatmentCategoryGroup !== undefined) {
    return namedEntityOutputName(
      specification.treatmentCategoryGroup,
      resultCount
    );
  }

  return cms.entities['any-type'];
}

export function generateWhere(
  cms: CmsBrowseResultsSummary,
  location?: Location
): string {
  const cmsResultsSummary = cms.page.browse['results-summary'];

  if (location === undefined) {
    return '';
  }

  if (location.point !== undefined) {
    return cmsResultsSummary['where-geolocation'];
  }

  const name = location.box
    ? cmsResultsSummary['where-location-box']
    : getLocationDisplayName(cms, location);
  const hasRadiusExpansion = Boolean(location.radius);

  const whereTemplate = hasRadiusExpansion
    ? cmsResultsSummary['where-near']
    : cmsResultsSummary.where;
  return substitute(whereTemplate, name);
}

export function getLocationDisplayName(
  cms: CmsBrowseResultsSummary,
  location?: Location
): string {
  const cmsResultsSummary = cms.page.browse['results-summary'];

  if (location === undefined) {
    return '';
  }

  let name;

  if (location.point !== undefined) {
    return cmsResultsSummary['where-geolocation'];
  }

  if (location.box !== undefined) {
    return cmsResultsSummary['where-location-box'];
  }

  if (location.tree) {
    name = location.tree.name;
  }
  if (location.postalReference) {
    name = location.postalReference.name;
  }
  if (location.external) {
    name = location.external.name;
  }
  if (name === undefined) {
    // don't believe that this will happen
    console.warn('Unknown location type for location', location);
    return '';
  }

  return name;
}

export function createBrowseResultsSummaryViewModel(
  cms: CmsBrowseResultsSummary,
  resultCount: number,
  specification: VenueListingSpecificationOutput,
  isBrand: boolean,
  titleOverride?: string
): string | undefined {
  if (resultCount === 0) {
    return;
  }

  const cmsResultsSummary = cms.page.browse['results-summary'];

  // template placeholder - {0}
  const count = resultCount.toString();
  // template placeholder - {1}
  const whatContent = generateWhat(
    cms,
    specification,
    resultCount,
    titleOverride
  ).replace(/ /g, nbsp);
  const what = isBrand ? whatContent : whatContent.toLowerCase();

  // template placeholder - {2}
  const where = generateWhere(cms, specification.location);

  if (specification.chain !== undefined) {
    return resultCount === 1
      ? substitute(cmsResultsSummary['summary-chain-singular'], what, where)
      : substitute(
          cmsResultsSummary['summary-chain-plural'],
          count,
          what,
          where
        );
  }

  if (specification.venueType !== undefined) {
    const template =
      resultCount === 1
        ? cmsResultsSummary['summary-venue-type-singular']
        : cmsResultsSummary['summary-venue-type-plural'];
    return substitute(template, count, what, where);
  }

  const template =
    resultCount === 1
      ? cmsResultsSummary['summary-singular']
      : cmsResultsSummary['summary-plural'];
  return substitute(template, count, what, where);
}
